export type NeighborhoodContent = {
  intro: string[]
  features: { title: string; description: string }[]
  testimonial: { name: string; quote: string }
}

export type Neighborhood = {
  slug: string
  name: string
  shortName: string
  seo: { title: string; description: string }
  streets: string[]
  en: NeighborhoodContent
  es: NeighborhoodContent
}

export const neighborhoods: Neighborhood[] = [
  {
    slug: 'mission-district-junk-removal',
    name: 'Mission District',
    shortName: 'Mission District',
    seo: {
      title:
        "Mission District Junk Removal & Hauling | Same-Day Service | Armando's",
      description:
        'Professional junk removal in San Francisco\'s Mission District. Same-day service, free estimates. Furniture, appliances, construction debris removal. Licensed & insured. Call 415-745-6692.',
    },
    streets: ['Valencia Street', 'Mission Street', 'Guerrero Street', '16th Street', '24th Street'],
    en: {
      intro: [
        'The Mission District is one of the densest, busiest corners of San Francisco — tight streets, historic Victorians above ground-floor storefronts, and parking that disappears by mid-morning. That combination is exactly why Mission District junk removal takes a crew that already knows the block.',
        'We work the Mission every week, so we plan the truck placement, the load-out path and the timing before we arrive. Whether it is a two-bedroom flat above a restaurant on Valencia or a mixed-use building on 24th, we get in, clear the job and get out without tying up the street.',
        'Same-day and next-day San Francisco junk removal is available throughout the Mission, with free upfront quotes and a fully bilingual crew.',
      ],
      features: [
        {
          title: 'Victorian flats and narrow staircases',
          description:
            'Many Mission buildings are Victorian with narrow hallways and tight staircases. We know how to safely remove large furniture, mattresses and appliances through challenging spaces without damaging trim or walls.',
        },
        {
          title: 'Street access and parking pressure',
          description:
            'Valencia, Mission and 24th stay busy all day. We schedule around peak traffic, work fast at the curb and keep the sidewalk clear while we load.',
        },
        {
          title: 'Mixed residential and commercial junk',
          description:
            'From restaurant equipment and retail fixtures to apartment cleanouts and renovation debris, Mission jobs are rarely just one thing. One crew handles all of it.',
        },
      ],
      testimonial: {
        name: 'Maria R.',
        quote:
          'I sent a photo of my flat on Valencia and they were there the same afternoon. Two floors of furniture gone in about an hour, and they swept the stairwell after.',
      },
    },
    es: {
      intro: [
        'El Mission District es una de las zonas más densas y activas de San Francisco: calles estrechas, edificios victorianos históricos sobre locales comerciales y estacionamiento que desaparece antes del mediodía. Por eso el retiro de basura en el Mission requiere un equipo que ya conozca la cuadra.',
        'Trabajamos en el Mission cada semana, así que planeamos dónde estacionar el camión, por dónde sacar todo y a qué hora llegar antes de empezar. Ya sea un departamento arriba de un restaurante en Valencia o un edificio de uso mixto en la 24, entramos, limpiamos y salimos sin bloquear la calle.',
        'Ofrecemos servicio el mismo día o al día siguiente en todo el Mission, con cotizaciones claras y un equipo totalmente bilingüe.',
      ],
      features: [
        {
          title: 'Edificios victorianos y escaleras angostas',
          description:
            'Muchos edificios del Mission son victorianos, con pasillos angostos y escaleras difíciles. Sabemos cómo sacar muebles grandes, colchones y electrodomésticos sin dañar paredes ni molduras.',
        },
        {
          title: 'Acceso y estacionamiento complicado',
          description:
            'Valencia, Mission y la 24 están ocupadas todo el día. Trabajamos rápido en la acera, evitamos las horas de más tráfico y dejamos la banqueta libre.',
        },
        {
          title: 'Trabajos residenciales y comerciales',
          description:
            'Desde equipo de restaurante y muebles de tienda hasta vaciado de departamentos y escombros de remodelación, en el Mission casi nunca es solo una cosa. Un solo equipo lo maneja todo.',
        },
      ],
      testimonial: {
        name: 'Maria R.',
        quote:
          'Mandé una foto de mi departamento en Valencia y llegaron esa misma tarde. Dos pisos de muebles en menos de una hora, y hasta barrieron la escalera.',
      },
    },
  },
  {
    slug: 'excelsior-junk-removal',
    name: 'Excelsior',
    shortName: 'Excelsior',
    seo: {
      title: "Excelsior Junk Removal & Hauling | San Francisco | Armando's",
      description:
        'Professional junk removal in Excelsior, San Francisco. Same-day service available. Free estimates for furniture, appliances, garage cleanouts & demolition. Licensed & insured.',
    },
    streets: ['Mission Street', 'Geneva Avenue', 'Persia Avenue', 'Ocean Avenue', 'Silver Avenue'],
    en: {
      intro: [
        'The Excelsior is our home base — we are based in 94112, which means Excelsior junk removal usually means a crew that is already minutes away. Most jobs here get same-day service.',
        'This is a neighborhood of single-family homes, in-law units and deep garages that have been collecting boxes, tools and old appliances for decades. Garage cleanouts and full estate cleanouts are the jobs we run most often in the Excelsior, and we handle them start to finish.',
        'Free estimates, upfront pricing and a bilingual crew that knows every street between Mission and McLaren Park.',
      ],
      features: [
        {
          title: 'Deep garages and decades of storage',
          description:
            'Excelsior garages hold a lot. We sort, carry and haul everything in one visit and leave the floor swept and usable.',
        },
        {
          title: 'Hillside driveways and back stairs',
          description:
            'Many homes here sit above the sidewalk with long stairs or a sloped driveway. Our crew is set up to carry heavy items up and down safely.',
        },
        {
          title: 'Family homes and in-law units',
          description:
            'Turnovers, remodels and downsizing jobs are common in the Excelsior. We clear furniture, appliances and renovation debris so the unit is ready to use.',
        },
      ],
      testimonial: {
        name: 'Carlos M.',
        quote:
          'Twenty years of junk in the garage off Persia and they cleared it in one morning. Fair price, no surprises, and everything explained in Spanish.',
      },
    },
    es: {
      intro: [
        'El Excelsior es nuestra base: estamos en el 94112, así que cuando alguien necesita retiro de basura en el Excelsior, normalmente estamos a minutos. La mayoría de los trabajos se hacen el mismo día.',
        'Es un barrio de casas familiares, unidades in-law y garages profundos que llevan décadas acumulando cajas, herramientas y electrodomésticos viejos. El vaciado de garages y de casas completas es el trabajo que más hacemos aquí, y lo manejamos de principio a fin.',
        'Estimados gratis, precios claros y un equipo bilingüe que conoce cada calle entre Mission y McLaren Park.',
      ],
      features: [
        {
          title: 'Garages profundos y años de cosas guardadas',
          description:
            'Los garages del Excelsior guardan mucho. Separamos, cargamos y retiramos todo en una sola visita y dejamos el piso barrido y listo para usar.',
        },
        {
          title: 'Entradas en pendiente y escaleras traseras',
          description:
            'Muchas casas están por encima de la banqueta, con escaleras largas o entradas inclinadas. Nuestro equipo está preparado para bajar y subir cosas pesadas con seguridad.',
        },
        {
          title: 'Casas familiares y unidades in-law',
          description:
            'Cambios de inquilino, remodelaciones y mudanzas son comunes aquí. Retiramos muebles, electrodomésticos y escombros para que la unidad quede lista.',
        },
      ],
      testimonial: {
        name: 'Carlos M.',
        quote:
          'Veinte años de cosas en el garage cerca de Persia y lo vaciaron en una mañana. Precio justo, sin sorpresas y todo explicado en español.',
      },
    },
  },
  {
    slug: 'bernal-heights-junk-removal',
    name: 'Bernal Heights',
    shortName: 'Bernal Heights',
    seo: {
      title: "Bernal Heights Junk Removal & Hauling | San Francisco | Armando's",
      description:
        'Expert junk removal in Bernal Heights, SF. Same-day service, free estimates. Furniture removal, construction debris, garage cleanouts. Licensed & insured. Se habla español.',
    },
    streets: ['Cortland Avenue', 'Mission Street', 'Precita Avenue', 'Folsom Street', 'Bocana Street'],
    en: {
      intro: [
        'Bernal Heights is built on a hill, and that changes everything about a haul. Steep one-way streets, short blocks, small cottages and staircases that switch back twice before the front door — Bernal Heights junk removal is a logistics job as much as a lifting job.',
        'We scout the approach before the truck rolls, park where we will not block a neighbor, and carry down in loads sized for the stairs. Renovation debris, old decking, garage clutter and full furniture removals are all routine here.',
        'Same-day and next-day service, free estimates and careful work on tight hillside lots.',
      ],
      features: [
        {
          title: 'Steep streets and switchback stairs',
          description:
            'Hillside access is the hardest part of any Bernal job. We bring the right crew size so heavy items come down safely, not fast and reckless.',
        },
        {
          title: 'Small lots and tight side yards',
          description:
            'There is rarely room to stage a pile. We load straight to the truck to keep your yard and sidewalk clear the whole time.',
        },
        {
          title: 'Remodel and deck debris',
          description:
            'Bernal is full of ongoing remodels. We clear lumber, drywall, old decking and fencing so your contractor can keep working.',
        },
      ],
      testimonial: {
        name: 'James T.',
        quote:
          'Got three quotes for a debris haul off Cortland. Armando was the most affordable and the only one who showed up the next day. Straightforward pricing, no surprises.',
      },
    },
    es: {
      intro: [
        'Bernal Heights está construido sobre una colina, y eso cambia todo el trabajo. Calles inclinadas de un solo sentido, cuadras cortas, casitas pequeñas y escaleras que dan dos vueltas antes de la puerta: el retiro de basura en Bernal Heights es tanto logística como fuerza.',
        'Revisamos el acceso antes de llegar con el camión, estacionamos sin bloquear a los vecinos y bajamos la carga en tandas adecuadas para la escalera. Escombros de remodelación, madera de decks viejos, cosas del garage y retiro completo de muebles son trabajo de todos los días aquí.',
        'Servicio el mismo día o al día siguiente, estimados gratis y trabajo cuidadoso en terrenos inclinados.',
      ],
      features: [
        {
          title: 'Calles inclinadas y escaleras difíciles',
          description:
            'El acceso en la colina es lo más difícil de cualquier trabajo en Bernal. Llevamos suficiente personal para bajar cosas pesadas con seguridad.',
        },
        {
          title: 'Terrenos pequeños y patios angostos',
          description:
            'Casi nunca hay espacio para hacer montones. Cargamos directo al camión para mantener tu patio y la banqueta libres.',
        },
        {
          title: 'Escombros de remodelación y decks',
          description:
            'Bernal siempre tiene remodelaciones en marcha. Retiramos madera, drywall, decks viejos y cercas para que tu contratista siga trabajando.',
        },
      ],
      testimonial: {
        name: 'James T.',
        quote:
          'Pedí tres cotizaciones para retirar escombros cerca de Cortland. Armando fue el más económico y el único que llegó al día siguiente. Precio claro, sin sorpresas.',
      },
    },
  },
  {
    slug: 'soma-junk-removal',
    name: 'SoMa',
    shortName: 'SoMa',
    seo: {
      title: "SoMa Junk Removal & Hauling | San Francisco | Armando's",
      description:
        'Professional junk removal in SoMa (South of Market), San Francisco. Same-day service for furniture, appliances, construction debris & more. Free estimates. Call 415-745-6692.',
    },
    streets: ['Folsom Street', 'Howard Street', 'Harrison Street', 'Brannan Street', '2nd, 3rd and 4th Streets'],
    en: {
      intro: [
        'SoMa is high-rise condos, converted lofts, offices and active construction all on the same block. SoMa junk removal usually means freight elevators, loading docks, building managers and a booked service window — and we work inside all of it.',
        'We coordinate with property management, protect elevators and corridors, and keep the dock clear for the next tenant. Office furniture, cubicles, appliances, e-waste and renovation debris are our most common South of Market jobs.',
        'Same-day and next-day San Francisco junk removal with free estimates and commercial-ready crews.',
      ],
      features: [
        {
          title: 'Freight elevators and service windows',
          description:
            'Most SoMa buildings require a reserved elevator and a set time slot. We book it, show up on time and finish inside the window.',
        },
        {
          title: 'Office and commercial cleanouts',
          description:
            'Desks, cubicles, chairs, server gear and e-waste from an office move or closure — we clear the whole floor, not just the easy items.',
        },
        {
          title: 'Construction and renovation debris',
          description:
            'SoMa is always under renovation. We haul drywall, framing, flooring and demo debris so your job site stays workable.',
        },
      ],
      testimonial: {
        name: 'Daniel K.',
        quote:
          'We closed an office off Folsom and needed it empty in two days. They handled the building paperwork, the freight elevator and every last desk.',
      },
    },
    es: {
      intro: [
        'SoMa son torres de condominios, lofts convertidos, oficinas y construcción activa en la misma cuadra. El retiro de basura en SoMa casi siempre implica elevadores de carga, muelles de descarga, administradores del edificio y un horario reservado, y nosotros trabajamos con todo eso.',
        'Coordinamos con la administración, protegemos elevadores y pasillos y dejamos el muelle libre para el siguiente inquilino. Muebles de oficina, cubículos, electrodomésticos, electrónica y escombros de remodelación son nuestros trabajos más comunes al sur del Market.',
        'Servicio el mismo día o al día siguiente, con estimados gratis y equipos listos para trabajos comerciales.',
      ],
      features: [
        {
          title: 'Elevadores de carga y horarios reservados',
          description:
            'La mayoría de los edificios de SoMa piden elevador reservado y un horario fijo. Lo agendamos, llegamos a tiempo y terminamos dentro del horario.',
        },
        {
          title: 'Vaciado de oficinas y locales',
          description:
            'Escritorios, cubículos, sillas, equipo de servidores y electrónica de una mudanza o cierre de oficina: vaciamos el piso completo, no solo lo fácil.',
        },
        {
          title: 'Escombros de construcción y remodelación',
          description:
            'SoMa siempre está en obra. Retiramos drywall, madera, pisos y escombros de demolición para que la obra siga avanzando.',
        },
      ],
      testimonial: {
        name: 'Daniel K.',
        quote:
          'Cerramos una oficina cerca de Folsom y la necesitábamos vacía en dos días. Se encargaron del papeleo del edificio, del elevador de carga y de cada escritorio.',
      },
    },
  },
  {
    slug: 'sunset-district-junk-removal',
    name: 'Sunset District',
    shortName: 'Sunset',
    seo: {
      title: "Sunset District Junk Removal & Hauling | San Francisco | Armando's",
      description:
        'Professional junk removal in Sunset District, San Francisco. Same-day service available. Free estimates for all junk removal needs. Licensed, insured & bilingual.',
    },
    streets: ['Taraval Street', 'Noriega Street', 'Judah Street', 'Irving Street', '19th Avenue and Sunset Boulevard'],
    en: {
      intro: [
        'The Sunset is block after block of family homes with tunnel-entrance garages, ground-floor rooms and long avenues. Sunset District junk removal is mostly garage cleanouts, appliance swaps and multi-generation household clearing — and those jobs reward a crew that is organized.',
        'We park on the avenue, work through the garage in order and keep the driveway clear so you can still get the car in that evening. Damp, fog-heavy storage means a lot of what comes out is beyond saving, and we sort it for you.',
        'Free estimates, same-day availability and a bilingual crew across the whole Sunset.',
      ],
      features: [
        {
          title: 'Tunnel-entrance garages',
          description:
            'Low ceilings and a single narrow entrance make Sunset garages awkward to empty. We break the load into runs that actually fit through the door.',
        },
        {
          title: 'Long avenues and street sweeping days',
          description:
            'We plan around sweeping and school traffic on the avenues so the truck is never in the way.',
        },
        {
          title: 'Fog damage and old appliances',
          description:
            'Damp garages ruin boxes, mattresses and old appliances. We haul the water-damaged items and leave the space dry and clear.',
        },
      ],
      testimonial: {
        name: 'Lisa W.',
        quote:
          'Cleared out my parents\u2019 garage off Noriega — thirty years of storage. They were patient, organized and the price never moved.',
      },
    },
    es: {
      intro: [
        'El Sunset son cuadras y cuadras de casas familiares con garages de entrada tipo túnel, cuartos en la planta baja y avenidas largas. El retiro de basura en el Sunset es principalmente vaciado de garages, cambio de electrodomésticos y limpieza de casas de varias generaciones, y esos trabajos necesitan un equipo organizado.',
        'Estacionamos en la avenida, avanzamos por el garage en orden y dejamos la entrada libre para que puedas meter el carro esa misma noche. Con la humedad y la niebla, mucho de lo que sale ya no se puede salvar, y nosotros lo separamos por ti.',
        'Estimados gratis, disponibilidad el mismo día y equipo bilingüe en todo el Sunset.',
      ],
      features: [
        {
          title: 'Garages de entrada tipo túnel',
          description:
            'Techos bajos y una sola entrada angosta hacen difícil vaciar los garages del Sunset. Dividimos la carga en tandas que sí pasan por la puerta.',
        },
        {
          title: 'Avenidas largas y días de barrido',
          description:
            'Planeamos alrededor del barrido de calles y el tráfico escolar para que el camión nunca estorbe.',
        },
        {
          title: 'Humedad y electrodomésticos viejos',
          description:
            'La humedad arruina cajas, colchones y electrodomésticos viejos. Retiramos todo lo dañado y dejamos el espacio seco y despejado.',
        },
      ],
      testimonial: {
        name: 'Lisa W.',
        quote:
          'Vaciaron el garage de mis papás cerca de Noriega, treinta años de cosas. Fueron pacientes, organizados y el precio nunca cambió.',
      },
    },
  },
  {
    slug: 'richmond-district-junk-removal',
    name: 'Richmond District',
    shortName: 'Richmond',
    seo: {
      title: "Richmond District Junk Removal & Hauling | San Francisco | Armando's",
      description:
        'Expert junk removal in Richmond District, SF. Same-day and next-day service available. Free estimates for furniture, appliances & construction debris. Licensed & insured.',
    },
    streets: ['Clement Street', 'Geary Boulevard', 'Balboa Street', 'California Street', 'Fulton Street'],
    en: {
      intro: [
        'The Richmond mixes Edwardian homes, basement garages and the busy small-business corridors along Clement and Geary. Richmond District junk removal often means working between parked cars, shared driveways and tenants upstairs.',
        'We handle basement and in-law cleanouts, furniture removal from upper flats, restaurant and retail clearing on Clement, and renovation debris from the remodels happening all over the neighborhood.',
        'Same-day and next-day service, upfront quotes and a licensed, insured, bilingual crew.',
      ],
      features: [
        {
          title: 'Edwardian flats and basement storage',
          description:
            'Low basement ceilings and steep interior stairs are the norm here. We remove bulky items without scraping walls or banisters.',
        },
        {
          title: 'Commercial corridors on Clement and Geary',
          description:
            'Restaurants and shops need clearing done fast and outside business hours. We work around your opening time.',
        },
        {
          title: 'Shared driveways and street parking',
          description:
            'Access is tight along Balboa and California. We stage the load carefully so neighbors keep their access the whole time.',
        },
      ],
      testimonial: {
        name: 'Andrea P.',
        quote:
          'They emptied a basement off Balboa that I had been avoiding for years. Quoted from a photo, arrived the next morning, done before lunch.',
      },
    },
    es: {
      intro: [
        'El Richmond mezcla casas eduardianas, garages en el sótano y los corredores comerciales activos de Clement y Geary. El retiro de basura en el Richmond casi siempre significa trabajar entre carros estacionados, entradas compartidas e inquilinos arriba.',
        'Hacemos vaciado de sótanos y unidades in-law, retiro de muebles de departamentos en pisos altos, limpieza de restaurantes y tiendas en Clement, y retiro de escombros de las remodelaciones que hay por todo el barrio.',
        'Servicio el mismo día o al día siguiente, cotizaciones claras y un equipo con licencia, seguro y bilingüe.',
      ],
      features: [
        {
          title: 'Casas eduardianas y sótanos',
          description:
            'Los techos bajos del sótano y las escaleras empinadas son lo normal aquí. Sacamos cosas grandes sin raspar paredes ni barandales.',
        },
        {
          title: 'Corredores comerciales de Clement y Geary',
          description:
            'Restaurantes y tiendas necesitan limpieza rápida y fuera del horario de atención. Trabajamos alrededor de tu hora de apertura.',
        },
        {
          title: 'Entradas compartidas y estacionamiento',
          description:
            'El acceso es estrecho en Balboa y California. Organizamos la carga con cuidado para que los vecinos nunca pierdan su acceso.',
        },
      ],
      testimonial: {
        name: 'Andrea P.',
        quote:
          'Vaciaron un sótano cerca de Balboa que llevaba años evitando. Cotizaron con una foto, llegaron a la mañana siguiente y terminaron antes del mediodía.',
      },
    },
  },
]

export const getNeighborhood = (slug: string) =>
  neighborhoods.find((n) => n.slug === slug)
